import {
    ColumnMetadata,
    DataGrid,
    DataGridRow,
    RowSizes,
    ColumnTypes,
} from "~/components/DataGrid";
import { Asset, AssetRating } from "~/modules/assets/types";
import { ApiQuery, SortDirections, SortQuery } from "~/modules/filters/types";
import { Emdash } from "~/components/Punctuation";
import { useState } from "react";
import { sortItemsByProperty } from "~/utils/functions/sortItemsByProperty";
import { ImageLoader } from "~/components/ImageLoader";
import { RWA_COIN_ICON } from "~/consts";
import { Link } from "~/components/Link";
import { AssetUserEvents } from "~/modules/analytics/events/assets";
import { extractDomainName } from "~/utils/extractDomainName";
import { faArrowUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// // // //

type AssetRatingRow = AssetRating & {
    key: string;
    assetTicker: string;
};

enum AssetRatingColumns {
    Provider = "provider",
    Type = "type",
    Rating = "rating",
    Date = "date",
    Document = "document",
}

// // // //

/**
 * ASSET_RATING_SORT_MAP
 * Map each AssetRatingColumns to a key used to in-memory sort the array of items using `sortItemsByNestedProperty`
 */
const ASSET_RATING_SORT_MAP: Partial<Record<AssetRatingColumns, string>> = {
    [AssetRatingColumns.Provider]: "provider.name",
    [AssetRatingColumns.Type]: "type",
    [AssetRatingColumns.Rating]: "rating",
    [AssetRatingColumns.Date]: "date",
    [AssetRatingColumns.Document]: "document",
};

// // // //

function useInMemoryAssetRatingSort(props: {
    rows: AssetRatingRow[];
    sort?: SortQuery;
}) {
    const {
        rows,
        sort = {
            field: AssetRatingColumns.Provider,
            direction: SortDirections.desc,
        },
    } = props;

    const [query, setQuery] = useState<ApiQuery>({
        sort,
        pagination: {
            page: 1,
            perPage: 25,
        },
    });

    const sortedItems = sortItemsByProperty({
        items: rows,
        field: ASSET_RATING_SORT_MAP[query.sort.field as AssetRatingColumns],
        direction: query.sort.direction,
        nullsLast: true,
    });

    return { query, setQuery, sortedItems };
}

// // // //

export const PROVIDER_COLUMN: ColumnMetadata<
    AssetRatingRow,
    AssetRatingColumns
> = {
    label: "Provider",
    slug: AssetRatingColumns.Provider,
    type: ColumnTypes.string,
    width: 180,
    render: (item) => {
        const provider = item.provider;

        // Return Emdash if no token
        if (!provider) return <Emdash />;

        // Return service provider name and icon
        return (
            <div className="flex items-center gap-2">
                {provider.icon_url && (
                    <ImageLoader
                        src={provider.icon_url}
                        alt={`${provider.name} icon`}
                        fallbackSrc={RWA_COIN_ICON}
                        className="h-4 w-4 min-w-4 rounded-full"
                    />
                )}
                <div className="dg-overflow">{provider.name}</div>
            </div>
        );
    },
};

// TODO - update this to display the type of rating once we have it from the API
// export const TYPE_COLUMN: ColumnMetadata<AssetRatingRow, AssetRatingColumns> = {
//     label: "Type",
//     slug: AssetRatingColumns.Type,
//     sortsOn: AssetRatingColumns.Type,
//     type: ColumnTypes.string,
//     width: 130,
//     visible: true,
//     loginRequired: true,
//     render: (item) => {
//         return <Emdash />;
//     },
// };

// TODO - update this to display the type of date of the rating once we have it from the API
// export const DATE_COLUMN: ColumnMetadata<AssetRatingRow, AssetRatingColumns> = {
//     label: "Date",
//     slug: AssetRatingColumns.Date,
//     sortsOn: AssetRatingColumns.Date,
//     type: ColumnTypes.string,
//     width: 130,
//     visible: true,
//     loginRequired: true,
//     render: (item) => {
//         return <Emdash />;
//     },
// };

export const RATING_COLUMN: ColumnMetadata<AssetRatingRow, AssetRatingColumns> =
    {
        label: "Rating",
        slug: AssetRatingColumns.Rating,
        type: ColumnTypes.string,
        width: 130,
        loginRequired: true,
        render: (item) => {
            return item.rating;
        },
    };

export const DOCUMENT_COLUMN: ColumnMetadata<
    AssetRatingRow,
    AssetRatingColumns
> = {
    label: "Document",
    slug: AssetRatingColumns.Document,
    type: ColumnTypes.string,
    width: 130,
    loginRequired: true,
    render: (item) => {
        const url = item.url;

        if (!url) return <Emdash />;

        // Get domain from URL
        const domain = extractDomainName(url);

        return (
            <Link
                href={item.url}
                variant="default"
                className="flex items-center gap-2"
                analyticsLabel={AssetUserEvents.AssetRatingsDocumentLinkClick}
                analyticsEventProperties={{
                    asset_ticker: item.assetTicker,
                    asset_rating_provider: item.provider.name,
                }}
            >
                View on {domain}
                <FontAwesomeIcon
                    icon={faArrowUpRightFromSquare}
                    className="w-3 h-3"
                />
            </Link>
        );
    },
};

// // // //

export function AssetRatingsTable(props: { asset: Asset }) {
    const { asset } = props;

    // Collect AssetRating rows w/ assetTicker (we capture the assetTicker in the analytics event)
    const rows: AssetRatingRow[] = asset.ratings
        ? asset.ratings.map((rating, index) => ({
              ...rating,
              key: `${rating.provider.name}-${rating.url}-${index}`,
              assetTicker: asset.ticker as string,
          }))
        : [];

    const { query, setQuery, sortedItems } = useInMemoryAssetRatingSort({
        rows,
        sort: {
            field: AssetRatingColumns.Provider,
            direction: SortDirections.asc,
        },
    });

    const columns: ColumnMetadata<AssetRatingRow, AssetRatingColumns>[] = [
        PROVIDER_COLUMN,
        // TYPE_COLUMN,
        RATING_COLUMN,
        // DATE_COLUMN,
        DOCUMENT_COLUMN,
    ];

    return (
        <DataGrid
            query={query}
            columns={columns}
            rows={sortedItems}
            rowKey="key"
            bordered
            flush
            size={RowSizes.small}
            loading={false}
            analyticsTableName="asset-individual-ratings"
            onChangeQuery={setQuery}
            enableItemsPerPageSelect
        >
            {({ row, rowIndex, scrollLeft }) => (
                <DataGridRow<AssetRatingRow, AssetRatingColumns>
                    row={row}
                    key={row.key}
                    columns={columns}
                    size={RowSizes.small}
                    bordered
                    scrollLeft={scrollLeft}
                    striped={rowIndex % 2 === 1}
                />
            )}
        </DataGrid>
    );
}
